/** OLS 조달 송장 PDF — 성우로지스 발송자 정보 */
import { toDateOnlyString } from './dateOnly';

export const PROCUREMENT_SUPPLIER = {
  name: '성우로지스(주)',
  phone: '464-2573 ~ 5',
  fax: '462-2807',
  manager: '최병수',
  address: '부산 사상구 광장로 10 화인빌딩 12층',
} as const;

export const PROCUREMENT_SHIPPER_NAME = '오뚜기';

export interface ProcurementInvoicePdfLine {
  lineMemo: string | null;
  itemName: string;
  boxQty: string | number | null;
  packUnit?: string | null;
  weightKg: number | null;
  palletQty: number | null;
  releaseLocation: string;
}

export interface ProcurementInvoicePdfData {
  shipmentDate: string;
  destinationName: string;
  destinationAddress: string;
  destinationPhoneLandline: string;
  destinationPhoneMobile: string;
  /** @deprecated 구 phones 필드 — 신규 필드가 비어 있을 때만 사용 */
  destinationPhones?: string;
  destinationManager: string;
  releaseLocation: string;
  vehicleNumber: string;
  driverContact: string;
  arrivalAt: string | null;
  memo: string;
  lines: ProcurementInvoicePdfLine[];
}

export function formatShipmentDateKo(dateStr: string): string {
  const s = toDateOnlyString(dateStr);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return s;
  return `${m[1]}년 ${Number(m[2])}월 ${Number(m[3])}일`;
}

export function formatArrivalAtKo(iso: string | null | undefined): string {
  if (!iso) return '';
  return formatShipmentDateKo(toDateOnlyString(iso));
}

export function resolveDestinationPhones(data: Pick<
  ProcurementInvoicePdfData,
  'destinationPhoneLandline' | 'destinationPhoneMobile' | 'destinationPhones'
>): { landline: string; mobile: string } {
  const landline = String(data.destinationPhoneLandline || '').trim();
  const mobile = String(data.destinationPhoneMobile || '').trim();
  if (landline || mobile) {
    return { landline, mobile };
  }

  const legacy = String(data.destinationPhones || '').trim();
  if (!legacy) return { landline: '', mobile: '' };

  const parts = legacy
    .split(/[\n,/;]+/)
    .map(s => s.trim())
    .filter(Boolean);

  if (parts.length === 0) return { landline: '', mobile: '' };
  if (parts.length === 1) {
    if (/^010/.test(parts[0])) return { landline: '', mobile: parts[0] };
    return { landline: parts[0], mobile: '' };
  }

  const mobilePart = parts.find(p => /^010/.test(p)) || '';
  const landlinePart = parts.find(p => !/^010/.test(p)) || parts[0];
  return { landline: landlinePart, mobile: mobilePart };
}

/** @deprecated resolveDestinationPhones 사용 */
export function formatPhonesDisplay(phones: string | null | undefined): string {
  if (!phones) return '';
  return phones
    .split(/[\n,;]+/)
    .map(s => s.trim())
    .filter(Boolean)
    .join(' / ');
}

export function formatInvoiceQuantity(line: ProcurementInvoicePdfLine): string {
  const parts: string[] = [];
  if (line.boxQty != null && String(line.boxQty).trim() !== '') {
    const display = formatShipmentQtyDisplay(line.boxQty, line.packUnit);
    if (display) parts.push(`(${display})`);
  }
  if (line.weightKg != null && Number.isFinite(Number(line.weightKg))) {
    parts.push(`${Number(line.weightKg).toLocaleString()}KG`);
  }
  if (line.palletQty != null && Number.isFinite(Number(line.palletQty))) {
    parts.push(`${line.palletQty} PLT`);
  }
  return parts.join(' ') || '';
}

export function formatInvoiceCellNumber(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(Number(value))) return '';
  return Number(value).toLocaleString();
}

/** 박스 열 — 숫자면 천단위, 문자(드럼·c/t 등)는 그대로 */
export function formatInvoiceBoxQty(value: string | number | null | undefined): string {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  if (!s) return '';
  const normalized = s.replace(/,/g, '');
  if (/^-?\d+(\.\d+)?$/.test(normalized)) {
    return Number(normalized).toLocaleString();
  }
  return s;
}

/** 출고조회·표시용 — 수량 + 포장 (예: 10 박스) */
export function formatShipmentQtyDisplay(
  boxQty: string | number | null | undefined,
  packUnit?: string | null
): string {
  const qty = formatInvoiceBoxQty(boxQty);
  if (!qty) return '';
  const unit = String(packUnit || '').trim();
  return unit ? `${qty} ${unit}` : qty;
}

/** 수기 송장처럼 품명·수량을 한 칸에 표시 */
export function formatInvoiceLineDisplay(line: ProcurementInvoicePdfLine): string {
  const name = String(line.itemName || '').trim();
  if (!name) return '';
  const qty = formatInvoiceQuantity(line);
  return qty ? `${name}  ${qty}` : name;
}

/** A4 세로 (96dpi 기준) */
export const PROCUREMENT_PDF_WIDTH = 794;
export const PROCUREMENT_PDF_HEIGHT = 1123;
export const PROCUREMENT_PDF_ROW_HEIGHT = 22;

/** 1페이지당 최대 품목 행 — 15개까지는 1장 */
export const PROCUREMENT_PDF_ITEMS_PER_PAGE = 15;

/** 1페이지 송장 — 품목이 적을 때 빈 행 최소 개수 */
export const PROCUREMENT_PDF_MIN_ROWS = 15;

const EMPTY_LINE = (): ProcurementInvoicePdfLine => ({
  lineMemo: null,
  itemName: '',
  boxQty: null,
  packUnit: null,
  weightKg: null,
  palletQty: null,
  releaseLocation: '',
});

export function padInvoiceLines(lines: ProcurementInvoicePdfLine[]): ProcurementInvoicePdfLine[] {
  const result = [...lines];
  while (result.length < PROCUREMENT_PDF_MIN_ROWS) {
    result.push(EMPTY_LINE());
  }
  return result;
}

export interface ProcurementInvoicePdfPage {
  pageIndex: number;
  totalPages: number;
  lines: ProcurementInvoicePdfLine[];
  lineStartIndex: number;
}

export function getInvoiceMemoFlags(memo: string): {
  memoExtra: string;
  hasMemo: boolean;
} {
  const memoExtra = String(memo || '').trim();
  return { memoExtra, hasMemo: Boolean(memoExtra) };
}

function padToRowCount(lines: ProcurementInvoicePdfLine[], rowCount: number): ProcurementInvoicePdfLine[] {
  const result = [...lines];
  while (result.length < rowCount) {
    result.push(EMPTY_LINE());
  }
  return result;
}

/** 품목 수에 따라 A4 페이지 분할 — 매 페이지 동일 레이아웃(헤더·도착지·수송·하단), 품목만 15개씩 */
export function buildInvoicePages(lines: ProcurementInvoicePdfLine[]): ProcurementInvoicePdfPage[] {
  const actual = lines.filter(l => String(l.itemName || '').trim());
  const items = actual.length > 0 ? actual : [EMPTY_LINE()];
  const totalPages = Math.max(1, Math.ceil(items.length / PROCUREMENT_PDF_ITEMS_PER_PAGE));
  const pages: ProcurementInvoicePdfPage[] = [];

  for (let p = 0; p < totalPages; p++) {
    const slice = items.slice(p * PROCUREMENT_PDF_ITEMS_PER_PAGE, (p + 1) * PROCUREMENT_PDF_ITEMS_PER_PAGE);
    const pageLines =
      totalPages === 1
        ? padInvoiceLines(slice)
        : padToRowCount(slice, PROCUREMENT_PDF_ITEMS_PER_PAGE);

    pages.push({
      pageIndex: p,
      totalPages,
      lines: pageLines,
      lineStartIndex: p * PROCUREMENT_PDF_ITEMS_PER_PAGE,
    });
  }

  return pages;
}
